// React.
import React, { useContext, useEffect, useMemo, useState } from 'react';

// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';
import ChartPlotLine from "../../../types/ChartPlotLine";
import Change from "../../../types/instance/Change";
import Event from "../../../types/instance/Event";

// Components.
import WidgetCard from "../../../component/instance/widget";
import SqlStatisticsTable from "../../../component/instance/table/SqlStatisticsTable";
import StatementDetailsPanel from "./StatementDetailsPanel";

// Helper.
import {
    fetchResults,
    getConvertedTimeToUTC
} from '../../../helpers/utils';
import SqlApi from '../../../api/instance/SqlApi';
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../helpers/timeRangeUtils";
import TimeSeriesChart from "../../../component/chart/echarts/TimeSeriesChart";
import { EChartPoint, EChartTimeSeries } from "../../../component/chart/echarts/types";
import Helper from "../../../helpers/Helper";
import ConditionalRender from "../../../helpers/ConditionalRender";
import { getInstanceExecutions, getInstanceTotalTime } from "./utils";
import Alert from "../../../types/instance/Alert";
import { CHART_DATA_TYPE } from "../../../component/Constants";
import { showError } from '../../../utils/showAlert';
import TabDisabledBanner from '../../../component/instance/TabDisabledBanner';
import MoreStatisticsPicker from '../../../component/instance/MoreStatisticsPicker';
import GroupedViewToggle from '../../../component/instance/GroupedViewToggle';
import { useSqlStatisticsSelection } from '../../../hooks/useSqlStatisticsSelection';

export default function SqlStatistics(props: {
    instance: InstanceTarget,
    instanceTypes: any[],
    chartPlotLines: ChartPlotLine[],
    changes: Change[],
    events: Event[],
    alerts: Alert[],
    isGroup?: boolean,
}) {
    const { instance, alerts, changes, events, chartPlotLines } = props;
    const timeRangeContext = useContext(TimeRangeContext)
    const isAutorefresh = timeRangeContext.timeRange.autorefresh

    const [loading, setLoading] = useState<boolean>(true);

    const [timeSeries, setTimeSeries] = useState<number[][]>([]);
    const [executionsSeries, setExecutionsSeries] = useState<number[][]>([]);

    const {
        supportedStatistics,
        setSupportedStatistics,
        statisticsToShow,
        updateSqlExtendedOptionsList,
        checkUncheckAll
    } = useSqlStatisticsSelection(instance.id);

    // Slideout panel state
    const [selectedStatement, setSelectedStatement] = useState<string | null>(null);
    const [selectedStatementType, setSelectedStatementType] = useState<'statement' | 'groupedstatement' | 'batch' | 'groupedbatch'>('statement');
    const [panelOpen, setPanelOpen] = useState(false);

    const openPanel = (statementId: string, statementType: 'statement' | 'groupedstatement' | 'batch' | 'groupedbatch' = 'statement') => {
        setSelectedStatement(statementId);
        setSelectedStatementType(statementType);
        setPanelOpen(true);
    };

    const closePanel = () => {
        setPanelOpen(false);
        setSelectedStatement(null);
    };

    const [executions, setExecutions] = useState<number>(0);
    const [previousExecutions, setPreviousExecutions] = useState<number>(0);

    const [totalInstanceTime, setTotalInstanceTime] = useState<number>(0);
    const [totalPreviousInstanceTime, setTotalPreviousInstanceTime] = useState<number>(0);

    const timeSeriesPeak = useMemo(() => {
        return Helper.getPeakValue(timeSeries.map(i => i[1]))
    }, [timeSeries])

    const executionPeak = useMemo(() => {
        return Helper.getPeakValue(executionsSeries.map(i => i[1]))
    }, [executionsSeries])

    const getExecutions = async (instanceId: number) => {
        const executionsResult = await getInstanceExecutions(instanceId, timeRangeContext)
        setExecutions(executionsResult.executions)
        setPreviousExecutions(executionsResult.previousExecutions)
    }

    const getTotalTime = async (instanceId: number) => {
        const executionsTimeResult = await getInstanceTotalTime(instanceId, timeRangeContext)
        setTotalInstanceTime(executionsTimeResult.totalInstanceTime)
        setTotalPreviousInstanceTime(executionsTimeResult.totalPreviousInstanceTime)
    }

    useEffect(() => {
        async function load() {
            try {
                await getExecutions(instance.id)
                await getTotalTime(instance.id)
                setLoading(true);
                const results: any[][] = await fetchResults(
                    [
                        SqlApi.fetchStatisticTime(buildApiQuery(timeRangeContext.timeRange), instance.id, instance.type)
                    ]);

                let executions: number[][] = []
                let duration: number[][] = []

                if (active && results[0].length) {
                    const supportedStatisticsList: any[][] = await fetchResults(
                        [
                            SqlApi.fetchStatisticName(props.instance.type, '&collect=true')
                        ]);

                    const filteredSupportedStatistics = supportedStatisticsList[0].filter(key => key !== 'duration' && key !== 'executions');

                    const stats: any[] = []

                    filteredSupportedStatistics.forEach(stat => {
                        stats.push({
                            name: stat,
                            data: results[0].map(item => [getConvertedTimeToUTC(item), item[stat]])
                        });
                    })

                    results[0].forEach(item => {
                        executions.push([getConvertedTimeToUTC(item), item.executions])
                        duration.push([getConvertedTimeToUTC(item), item.duration])
                    })

                    setSupportedStatistics(stats)
                    setExecutionsSeries(executions)
                    setTimeSeries(duration)
                }
                setLoading(false);
            } catch (x: any) {
                console.log(x.message);
                const errorMessage = x.message || x.toString();
                showError('Failed to load SQL statistics', errorMessage);
                setLoading(false);
            }

            return true;
        }

        let active: boolean = true;
        load();
        return () => {
            // Clean up afterwards to avoid race conditions.
            active = false;
        }
    }, [timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, instance.id, instance.type]);

    const timeChartSeries = useMemo<EChartTimeSeries[]>(() => [
        { name: 'Duration', chartType: 'bar', color: '#69d1bf', data: timeSeries as EChartPoint[] },
        { name: 'Executions', chartType: 'line', color: '#ff853d', data: executionsSeries as EChartPoint[], yAxisIndex: 1 },
    ], [timeSeries, executionsSeries]);

    const timeChart = (
        <TimeSeriesChart series={timeChartSeries} tooltipType={CHART_DATA_TYPE.TIME}
                         plotLines={chartPlotLines} legend={true}/>
    );

   const getChart = ((statisticData: { name: string, data: [] }) => {
        const tooltipType = statisticData.name.includes('time') ? CHART_DATA_TYPE.TIME : CHART_DATA_TYPE.GENERIC;
        return <TimeSeriesChart key={statisticData.name}
                                series={[{ name: statisticData.name, chartType: 'bar', data: statisticData.data as EChartPoint[] }]}
                                tooltipType={tooltipType} plotLines={chartPlotLines}/>;
    });

    return (<>
        <TabDisabledBanner instance={instance} instanceTypes={props.instanceTypes} collectFlag={!!instance.collectsqlstatistics} defaultCollectKey="defaultcollectsqlstatistics" featureName="SQL Statistics" />
        <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 flexlist">
            <WidgetCard
                name="Total Time"
                type='time'
                iconClassName="time"
                icon="fa-stopwatch"
                value={totalInstanceTime}
                previous={totalPreviousInstanceTime}
                cardTooltipText="The total time spent"
            />

            <WidgetCard
                name="Executions"
                iconClassName="executions"
                icon="fa-chart-line"
                value={executions}
                previous={previousExecutions}
                cardTooltipText="The total number of executions"
            />

            <WidgetCard
                name="Average Time"
                type='time'
                iconClassName="averagetime"
                icon="fa-clock"
                value={executions > 0 ? totalInstanceTime / executions : 0}
                previous={previousExecutions > 0 ? totalPreviousInstanceTime / previousExecutions : undefined}
                cardTooltipText="The average time per execution"
            />

            <WidgetCard
                name="Events"
                events={{
                    alerts: alerts.length,
                    changes: changes.length,
                    custom: events.length
                }}
            />
        </div>
        <div className="row row-cols-1">
            <div id="statements" className="tab-pane fade show active" role="tabpanel"
                aria-labelledby="statements-tab">

                <div className="card collapsible">
                    <div className="card-header">
                        <i className="fal fa-chart-bar fa-fw" aria-hidden="true" />
                        Duration (ms) and Executions
                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                            data-bs-target={"#collapse-duration"} aria-expanded="false"
                            aria-controls={"collapse-duration"} />
                        <MoreStatisticsPicker
                            supportedStatistics={supportedStatistics}
                            statisticsToShow={statisticsToShow}
                            onToggle={updateSqlExtendedOptionsList}
                            onCheckUncheckAll={checkUncheckAll}
                        />
                        <GroupedViewToggle section="sql-statistics" instanceId={props.instance.id} isGroup={!!props.isGroup} tab="duration" className="me-1 mt-1"/>
                    </div>

                    <div id={"collapse-duration"} className="card-body collapse show">
                        <ConditionalRender if={loading && !isAutorefresh}>
                            <div className="w-100 text-center text-muted mt-3">
                                <div className="loader spinner chartSpinner">
                                </div>
                                <p className="mt-3">
                                    Loading data...
                                </p>
                            </div>
                        </ConditionalRender>
                        <ConditionalRender if={isAutorefresh || !loading}>
                            <p className="chart-peak">
                                <React.Fragment>Peak
                                    Time: {Helper.getTimeInEnglish(timeSeriesPeak)}</React.Fragment>,&nbsp;
                                <React.Fragment>Peak
                                    Executions: {executionPeak.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</React.Fragment>
                            </p>
                            <br />
                            {timeChart}
                        </ConditionalRender>
                    </div>
                </div>

                {statisticsToShow.map((stat) =>
                    <div key={stat.name} className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-chart-bar fa-fw" aria-hidden="true" />
                            {stat.name.replaceAll('_', ' ')}
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                data-bs-target={`#${stat.name}`} aria-expanded="false"
                                aria-controls={stat.name} />
                        </div>
                        <div id={stat.name} className="card-body collapse show mt-5">
                            <ConditionalRender if={loading}>
                                <div className="w-100 text-center text-muted mt-3">
                                    <div className="loader spinner chartSpinner">
                                    </div>
                                    <p className="mt-3">
                                        Loading data...
                                    </p>
                                </div>
                            </ConditionalRender>

                            <ConditionalRender if={!loading}>
                                {getChart(stat)}
                            </ConditionalRender>
                        </div>
                    </div>)}

                <SqlStatisticsTable instance={props.instance} isGroup={props.isGroup} onStatementClick={openPanel}/>
            </div>
        </div>
{/* Slide-out panel for statement details */}
                <StatementDetailsPanel
                    statementId={selectedStatement}
                    instance={instance}
                    open={panelOpen}
                    onClose={closePanel}
                    statementType={selectedStatementType}
                    onStatementClick={openPanel}
                />
            </>
    )
}
